import { useMemo, useState } from 'react';
import { usePrefs } from '../contexts/PrefsContext';
import { usePlay } from '../contexts/PlayContext';
import { games } from '../data/games';
import AddCustomLink from './AddCustomLink';
import FavoriteGame from './FavoriteGame';
import SectionBlock from './SectionBlock';
import HeartIcon from './HeartIcon';
import Modal from './Modal';
import { useNavigate } from 'react-router-dom';
import { debugLog } from '../utils/debugLogger';
import { isPageBreakId, nextPageBreakId } from '../utils/pageBreakUtils';

// A stable key for an item in the list, used both for React keys and for the
// stored ordering.
const itemKey = (item) => (item.isCustom ? `custom-${item.id}` : `game-${item.id}`);

// "My Games" - the user's shortlist, shown at the top of Home.
//
// It owns the list and nothing else. The zero-state and the sign-up nudge that
// used to live here are rungs 1 and 4 of HomeMessage's ladder now (D41), so
// this renders nothing at all when there's nothing shortlisted.
function FavoriteGamesList() {
  const {
    favorites,
    favoriteOrder,
    customLinks,
    addFavorite,
    removeFavorite,
    removeCustomLink,
    saveFavoriteOrder,
    prefsLoading,
    prefsError
  } = usePrefs();
  const { gamesPlayedToday, hasScoreForToday, handleGameLinkClick } = usePlay();
  const navigate = useNavigate();

  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddCustomLink, setShowAddCustomLink] = useState(false);

  // Which page breaks the viewer has clicked "View More" on this page load.
  // Purely local UI state, like edit mode itself - it resets on reload and
  // isn't meant to persist (unlike the list's order, which is saved).
  const [revealedBreaks, setRevealedBreaks] = useState(() => new Set());
  const revealBreak = (id) => setRevealedBreaks((prev) => new Set(prev).add(id));

  // While rows are being dragged around this holds the working order; it's
  // committed in a single call on "Done editing" rather than once per move
  // (D22 - in-flight edits stay in memory).
  const [draftOrder, setDraftOrder] = useState(null);

  const orderedItems = useMemo(() => {
    const favoriteGames = favorites
      .map(favoriteId => isPageBreakId(favoriteId)
        ? { id: favoriteId, isPageBreak: true }
        : games.find(g => g.id === favoriteId))
      .filter(Boolean);

    const allItems = [...favoriteGames, ...customLinks];
    const order = draftOrder ?? favoriteOrder;
    if (order.length === 0) return allItems;

    const remaining = [...allItems];
    const ordered = [];

    order.forEach(key => {
      const index = remaining.findIndex(item => itemKey(item) === key);
      if (index !== -1) ordered.push(remaining.splice(index, 1)[0]);
    });

    // Anything added since the order was saved goes on the end.
    ordered.push(...remaining);
    return ordered;
  }, [favorites, customLinks, favoriteOrder, draftOrder]);

  // Move item in the list (direction: -1 for up, 1 for down)
  const handleMove = (index, direction) => {
    debugLog("Move");
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= orderedItems.length) return;

    const reordered = [...orderedItems];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];

    setDraftOrder(reordered.map(itemKey));
  };

  const handleToggleEditMode = () => {
    if (isEditMode) {
      // Leaving edit mode - commit the reordering, if it changed.
      if (draftOrder) {
        saveFavoriteOrder(draftOrder);
        setDraftOrder(null);
      }
      setIsEditMode(false);
    } else {
      setIsEditMode(true);
    }
  };

  // Handle add score button clicks. No `from` state: score entry always
  // returns to Home now, whichever way you got there (4.8).
  const handleAddScore = (gameId) => {
    navigate(`/edit-score/${gameId}`);
  };

  const handleInsertPageBreak = () => {
    addFavorite(nextPageBreakId(favorites));
  };

  // Outside edit mode, a page break hides everything after it (up to and
  // including the next page break) until its own "View More" is clicked.
  // Edit mode ignores all of this and always shows the full list, so nothing
  // can be moved or deleted while it's hidden.
  //
  // Walked fresh on every render rather than kept as state, since it's a
  // pure function of orderedItems + which breaks have been revealed.
  const visibleItems = isEditMode
    ? orderedItems
    : (() => {
        const visible = [];
        let hidden = false;
        for (const item of orderedItems) {
          if (hidden) {
            // A still-hidden nested page break stays hidden too - it only
            // becomes its own "View More" once the section it's in opens.
            continue;
          }
          visible.push(item);
          if (item.isPageBreak && !revealedBreaks.has(item.id)) hidden = true;
        }
        return visible;
      })();

  // Hold the slot while the first fetch is in flight, rather than flashing an
  // empty list at a returning visitor who does have favorites (D25 - no cache,
  // so this round trip is unavoidable).
  if (prefsLoading) return null;

  // Nothing shortlisted yet - HomeMessage's rung 1 or 2 fills the space.
  if (orderedItems.length === 0) return null;

  return (
    <SectionBlock
      title={<><HeartIcon filled color="currentColor" className="align-middle w-3 h-3 -mt-1 mr-[2px]" /> Daily Playlist</>}
    >
      {prefsError && (
        <p className="mx-1 mb-1 px-3 py-2 rounded-lg text-sm text-nlg-cream bg-red-900/40" role="alert">
          {prefsError}
        </p>
      )}

      <ul className="list-none">
        {visibleItems.map((game) => {

          // Two distinct states, deliberately (D41, 4.3): a play row exists
          // for today (written on any game-link click) dims the row, and a
          // score logged for today additionally dims the Enter Score button.
          // Without the second, a dimmed row beside a bright gold button reads
          // as a rendering bug rather than "you played - now log it".
          const dimGame = gamesPlayedToday.includes(game.id);
          const dimScoreButton = hasScoreForToday(game.id);

          return <FavoriteGame
            key={itemKey(game)}
            game={game}
            index={orderedItems.indexOf(game)}
            totalGames={orderedItems.length}
            isEditMode={isEditMode}
            onMove={handleMove}
            onGameLinkClick={handleGameLinkClick}
            onDeleteCustomLink={removeCustomLink}
            onRemoveFavorite={removeFavorite}
            onAddScore={handleAddScore}
            dimGame={dimGame}
            dimScoreButton={dimScoreButton}
            isRevealed={game.isPageBreak && revealedBreaks.has(game.id)}
            onReveal={() => revealBreak(game.id)}
          />
        })}

        {/* Add page break / Add Custom Link - last items in the list while
            editing. The break inserts immediately, same as a custom link is
            immediately committed rather than staged (D22's staging is only
            for reordering). */}
        {isEditMode && (
          <>
            <li className="border-b border-white/5 last:border-none">
              <button
                onClick={handleInsertPageBreak}
                className="w-full flex items-center gap-3 py-2 px-3 text-left transition-colors duration-150 hover:bg-white/5"
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex-shrink-0 flex items-center justify-center gap-1 border border-dashed border-nlg-rule text-nlg-muted">
                  <span className="w-[4px] h-[4px] rounded-full bg-current" />
                  <span className="w-[4px] h-[4px] rounded-full bg-current" />
                  <span className="w-[4px] h-[4px] rounded-full bg-current" />
                </div>
                <span className="font-display font-bold text-sm sm:text-base text-nlg-muted">Add page break</span>
              </button>
            </li>

            <li className="border-b border-white/5 last:border-none">
              <button
                onClick={() => setShowAddCustomLink(true)}
                className="w-full flex items-center gap-3 py-2 px-3 text-left transition-colors duration-150 hover:bg-white/5"
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex-shrink-0 flex items-center justify-center text-xl border border-dashed border-nlg-rule text-nlg-muted">
                  +
                </div>
                <span className="font-display font-bold text-sm sm:text-base text-nlg-muted">Add custom link</span>
              </button>
            </li>
          </>
        )}
      </ul>

      <div className="px-4 py-1.5 border-t border-nlg-rule">
        <button
          onClick={handleToggleEditMode}
          className="text-sm font-bold text-nlg-gold transition-colors duration-150"
        >
          {isEditMode ? 'Done editing' : 'Manage list'}
        </button>
      </div>

      <Modal isOpen={showAddCustomLink} onClose={() => setShowAddCustomLink(false)} title="Add Custom Link">
        <AddCustomLink onAdded={() => setShowAddCustomLink(false)} />
      </Modal>
    </SectionBlock>
  );
}

export default FavoriteGamesList;
